import React, { useState, useEffect } from 'react';
import { getClubs, createClub, updateClub, deleteClub, getOrganizers } from '../../services/api';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';

const ManageClubsGlobal = () => {
    const [clubs, setClubs] = useState([]);
    const [organizers, setOrganizers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentClub, setCurrentClub] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        city: '',
        address: '',
        organizerId: '',
        slug: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch ALL clubs from Firestore
            const clubsData = await getClubs({ fetchAllFirestore: true });
            
            // Fetch ALL organizers for the dropdown
            const organizersData = await getOrganizers();
            
            setClubs(clubsData);
            setOrganizers(organizersData);
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Error al cargar los datos");
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value.toLowerCase());
    };

    const filteredClubs = clubs.filter(club => 
        club.name?.toLowerCase().includes(searchTerm) ||
        club.city?.toLowerCase().includes(searchTerm) ||
        club.slug?.toLowerCase().includes(searchTerm)
    );

    const openModal = (club = null) => {
        if (club) {
            setCurrentClub(club);
            setFormData({
                name: club.name || '',
                city: club.city || '',
                address: club.address || '',
                organizerId: club.organizerId || '',
                slug: club.slug || ''
            });
        } else {
            setCurrentClub(null);
            setFormData({
                name: '',
                city: '',
                address: '',
                organizerId: '',
                slug: ''
            });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (currentClub && !currentClub.id.toString().startsWith('json-')) {
                // UPDATE existing Firestore club
                await updateClub(currentClub.id, formData);
                toast.success('Club actualizado correctamente');
            } else {
                // CREATE new club (or IMPORT from JSON)
                await createClub(formData);
                toast.success(currentClub ? 'Club importado a la base de datos' : 'Club creado correctamente');
            }
            setIsModalOpen(false);
            fetchData();
        } catch (error) {
            console.error("Error saving club:", error);
            toast.error("Error al guardar el club");
        }
    };

    const handleDelete = async (id) => {
        if (id.toString().startsWith('json-')) {
            toast.error("No se pueden eliminar clubes del archivo estático (JSON).");
            return;
        }

        if (!window.confirm('¿Estás seguro de que quieres eliminar este club? Esta acción no se puede deshacer.')) return;
        
        try {
            await deleteClub(id);
            toast.success('Club eliminado');
            fetchData();
        } catch (error) {
            console.error("Error deleting club:", error);
            toast.error("Error al eliminar");
        }
    };

    const getOrganizerName = (id) => {
        const org = organizers.find(o => o.id === id);
        return org ? `${org.displayName || org.email}` : 'Desconocido / Sin asignar';
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 font-display">Gestión de Clubes</h1>
                    <p className="text-sm text-gray-500">Base de datos global de instalaciones (Firestore + JSON)</p>
                </div>
                <button 
                    onClick={() => openModal()}
                    className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                    + Nuevo Club
                </button>
            </div>

            {/* Search */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <input
                    type="text"
                    placeholder="Buscar por nombre, ciudad o slug..."
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={searchTerm}
                    onChange={handleSearch}
                />
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ciudad</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Origen</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organizador</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                        Cargando clubes...
                                    </td>
                                </tr>
                            ) : filteredClubs.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                        No se encontraron clubes.
                                    </td>
                                </tr>
                            ) : (
                                filteredClubs.map((club) => {
                                    const isJson = club.id.toString().startsWith('json-');
                                    return (
                                        <tr key={club.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{club.name}</div>
                                                <div className="text-xs text-gray-500 truncate max-w-[200px]">{club.address}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {club.city}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${isJson ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
                                                    {isJson ? 'Estático (JSON)' : 'Base de Datos'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${club.organizerId ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                                                    {getOrganizerName(club.organizerId)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button 
                                                    onClick={() => openModal(club)}
                                                    className="text-primary-600 hover:text-primary-900 mr-3"
                                                >
                                                    {isJson ? 'Importar/Editar' : 'Editar'}
                                                </button>
                                                {!isJson && (
                                                    <button 
                                                        onClick={() => handleDelete(club.id)}
                                                        className="text-red-500 hover:text-red-700"
                                                    >
                                                        Eliminar
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl animate-scale-in">
                        <h2 className="text-xl font-bold mb-4">
                            {currentClub ? 'Editar Club' : 'Nuevo Club'}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Club</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad</label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                        value={formData.city}
                                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Slug (URL)</label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                        value={formData.slug}
                                        onChange={(e) => setFormData({...formData, slug: e.target.value})}
                                        placeholder="ej: club-padel-madrid"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Dirección Completa</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                    value={formData.address}
                                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                                    placeholder="Calle Ejemplo 123"
                                />
                                <p className="text-xs text-gray-400 mt-1">Se intentará geolocalizar automáticamente al guardar.</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Asignar a Organizador</label>
                                <select
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                    value={formData.organizerId}
                                    onChange={(e) => setFormData({...formData, organizerId: e.target.value})}
                                >
                                    <option value="">-- Sin asignar (Global) --</option>
                                    {organizers.map(org => (
                                        <option key={org.id} value={org.id}>
                                            {org.displayName || org.email}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors"
                                >
                                    {currentClub ? 'Guardar Cambios' : 'Crear Club'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageClubsGlobal;
